import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
    CSS2DRenderer,
    CSS2DObject
} from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { createGradientTexture } from "../modules/3d.js";
import { showToast } from "../modules/toast.js";
import { decodeNestedDictBase64 } from "../modules/encoding.js";
import {
    estimateProcessingTime,
    updateProgressBar
} from "../modules/processingtime.js";
import { globalState } from "./globals.js";
import { createLabelsJSON } from "../modules/labelsoutput.js";
import { processingTimeHistory } from "../modules/processingtime.js";
import toothClasses from "../../data/tooth_classes.json";
import { ApiService } from "./api.js";

// Function to initialize multi-view rendering
function initMultiViewRendering() {
    const multiViewContainer = document.getElementById("multi-view-container");
    multiViewContainer.style.display = "grid";

    // Hide the main rendering
    globalState.renderer.domElement.style.display = "none";
    globalState.labelRenderer.domElement.style.display = "none";

    // Setup view titles
    document
        .getElementById("original-view")
        .setAttribute("data-title", "Original Model");
    document
        .getElementById("segmented-view")
        .setAttribute("data-title", "Segmented Model");
    document
        .getElementById("analysis-view")
        .setAttribute("data-title", "Teeth");
    document
        .getElementById("final-view")
        .setAttribute("data-title", "Final Result");

    // Initialize each view
    const views = ["original", "segmented", "analysis", "final"];

    views.forEach((view) => {
        // Create scene
        globalState.multiViewScenes[view] = new THREE.Scene();
        globalState.multiViewScenes[view].background = createGradientTexture();

        // Create camera
        globalState.multiViewCameras[view] = new THREE.PerspectiveCamera(
            75,
            window.innerWidth / 2 / (window.innerHeight / 2),
            0.1,
            1000
        );
        globalState.multiViewCameras[view].position.set(0, 0, 60);

        // Create renderer
        globalState.multiViewRenderers[view] = new THREE.WebGLRenderer({
            antialias: true
        });
        globalState.multiViewRenderers[view].setSize(
            window.innerWidth / 2,
            window.innerHeight / 2
        );
        document
            .getElementById(`${view}-view`)
            .appendChild(globalState.multiViewRenderers[view].domElement);

        // Create CSS2D label renderer
        globalState.multiViewLabelRenderers[view] = new CSS2DRenderer();
        globalState.multiViewLabelRenderers[view].setSize(
            window.innerWidth / 2,
            window.innerHeight / 2
        );
        globalState.multiViewLabelRenderers[view].domElement.style.position =
            "absolute";
        globalState.multiViewLabelRenderers[view].domElement.style.top = "0";
        globalState.multiViewLabelRenderers[view].domElement.style.left = "0";
        globalState.multiViewLabelRenderers[
            view
        ].domElement.style.pointerEvents = "none";
        document
            .getElementById(`${view}-view`)
            .appendChild(globalState.multiViewLabelRenderers[view].domElement);

        // Create controls
        globalState.multiViewControls[view] = new OrbitControls(
            globalState.multiViewCameras[view],
            globalState.multiViewRenderers[view].domElement
        );
        globalState.multiViewControls[view].enableDamping = true;
        globalState.multiViewControls[view].dampingFactor = 0.5;

        // Add lights with brightness setting
        const ambientLight = new THREE.AmbientLight(
            0xffffff,
            0.4 * globalState.brightnessAmount
        );
        globalState.multiViewScenes[view].add(ambientLight);

        const directionalLight1 = new THREE.DirectionalLight(
            0xffffff,
            0.6 * globalState.brightnessAmount
        );
        directionalLight1.position.set(1, 1, 1);
        globalState.multiViewScenes[view].add(directionalLight1);

        const directionalLight2 = new THREE.DirectionalLight(
            0xffffff,
            0.4 * globalState.brightnessAmount
        );
        directionalLight2.position.set(-1, -1, -1);
        globalState.multiViewScenes[view].add(directionalLight2);
    });

    // Link all views' camera controls
    views.forEach((view) => {
        globalState.multiViewControls[view].addEventListener("change", () => {
            views.forEach((otherView) => {
                if (otherView !== view) {
                    globalState.multiViewCameras[otherView].position.copy(
                        globalState.multiViewCameras[view].position
                    );
                    globalState.multiViewCameras[otherView].rotation.copy(
                        globalState.multiViewCameras[view].rotation
                    );
                    globalState.multiViewControls[otherView].target.copy(
                        globalState.multiViewControls[view].target
                    );
                }
            });
        });
    });

    // Add render function to animation loop
    globalState.renderMultiView = true;
}

// Function to handle teeth separation
async function processSeparation() {
    const processingIndicator = document.getElementById("processing-indicator");
    processingIndicator.style.display = "block";

    // Reset progress bar
    updateProgressBar(0);

    try {
        // Update task label
        document.querySelector(
            "#processing-indicator .progress-task"
        ).textContent = "Processing Teeth Reconstruction...";

        // Use different approach based on separation phase
        let file;
        let jawSelection;
        let labelsFile = null;

        // We're continuing from segmentation step (phase 1 or 2)
        if (
            (globalState.separationPhase === 1 ||
                globalState.separationPhase === 2) &&
            globalState.lastInferenceResult
        ) {
            console.log("Using existing segmentation results");
            console.log(
                "Current separation phase:",
                globalState.separationPhase
            );
            file = document.getElementById("file-select").files[0];
            const jawElement1 = document.querySelector(
                "#jaw-selection-options .jaw-button.active"
            );
            if (!jawElement1) {
                throw new Error(
                    "Please select a jaw type (Upper or Lower) before processing"
                );
            }
            jawSelection = jawElement1.getAttribute("data-jaw");
        }
        // We're starting fresh with reconstruction
        else {
            console.log("Starting fresh reconstruction");
            const fileInput = document.getElementById("file-select");
            if (!fileInput.files || !fileInput.files[0]) {
                throw new Error("Please select an OBJ file first");
            }
            file = fileInput.files[0];
            const jawElement2 = document.querySelector(
                "#jaw-selection-options .jaw-button.active"
            );
            if (!jawElement2) {
                throw new Error(
                    "Please select a jaw type (Upper or Lower) before processing"
                );
            }
            jawSelection = jawElement2.getAttribute("data-jaw");

            // Ask for labels JSON file
            // Create an input file element for the JSON file
            const jsonFileInput = document.createElement("input");
            jsonFileInput.type = "file";
            jsonFileInput.accept = ".json";
            jsonFileInput.style.display = "none";
            document.body.appendChild(jsonFileInput);

            // Create a promise to handle the file selection
            const jsonFilePromise = new Promise((resolve, reject) => {
                jsonFileInput.onchange = (e) => {
                    if (e.target.files.length > 0) {
                        resolve(e.target.files[0]);
                    } else {
                        reject(new Error("No JSON file selected"));
                    }
                };

                // If the user cancels, we'll reject after a timeout
                setTimeout(() => {
                    if (!jsonFileInput.files.length) {
                        reject(new Error("JSON file selection timed out"));
                    }
                }, 60000); // 1 minute timeout
            });

            // Prompt user to select JSON file
            jsonFileInput.click();

            showToast("Please select the labels JSON file", "info");

            try {
                labelsFile = await jsonFilePromise;
                showToast(
                    `Selected JSON file: ${labelsFile.name}`,
                    "check-circle"
                );
            } catch (error) {
                throw new Error("Failed to load labels file: " + error.message);
            } finally {
                document.body.removeChild(jsonFileInput);
            }
        }

        // Estimate processing time
        const estimatedTime = estimateProcessingTime(
            file.size,
            "teethseparation"
        );

        // Record start time
        const startTime = Date.now();

        // Prepare labels data for reconstruction
        let jsonFile = null;

        if (
            (globalState.separationPhase === 1 ||
                globalState.separationPhase === 2) &&
            globalState.lastInferenceResult
        ) {
            // If we're in phase 1 or 2 (segmentation done) and have results, create and add the labels file
            console.log("Creating labels file from inference results");
            // Only include necessary fields for reconstruction: is_compressed, labels, instance_ids
            const labelsData = createLabelsJSON([
                "is_compressed",
                "labels",
                "instance_ids"
            ]);
            const labelsBlob = new Blob([JSON.stringify(labelsData, null, 2)], {
                type: "application/json"
            });
            jsonFile = new File(
                [labelsBlob],
                file.name.replace(/\.[^/.]+$/, "") + "_labels.json",
                { type: "application/json" }
            );
        } else if (labelsFile) {
            // Use the labels file selected by the user
            console.log("Using selected labels file:", labelsFile.name);
            jsonFile = labelsFile;
        }

        // Validate file type before uploading (reconstruction requires PREPARED file)
        try {
            const validationResponse = await ApiService.validateFileType(
                file,
                null, // azurePath not used
                "teethml", // Same requirements as teethml
                jawSelection
            );

            if (validationResponse && validationResponse.warning) {
                showToast(
                    validationResponse.warning,
                    "exclamation-triangle",
                    5000
                );
            }
        } catch (error) {
            console.warn(
                "Failed to validate file type for reconstruction:",
                error
            );
            // Don't block reconstruction if validation fails
        }

        // Use the ApiService to upload for reconstruction
        const uploadResponse = await ApiService.uploadForReconstruction(
            file, // meshFile
            null, // meshAzurePath (not using Azure)
            jsonFile, // labelsFile
            null // labelsAzurePath (not using Azure)
        );

        // Show warning if present in response
        if (uploadResponse && uploadResponse.warning) {
            showToast(uploadResponse.warning, "exclamation-triangle", 5000);
        }

        const itemId = uploadResponse.item_id;
        globalState.lastProcessedItemId = itemId;

        // Use the ApiService to poll for status until completed
        const processingResult = await ApiService.pollUntilCompletion(
            itemId,
            updateProgressBar,
            estimatedTime,
            startTime
        );

        // Store the full processing result in global state
        if (!globalState.separationData) {
            globalState.separationData = {};
        }
        globalState.separationData.processingResult = processingResult;

        // Record processing time
        const processingTimeInSeconds = (Date.now() - startTime) / 1000;
        processingTimeHistory["teethseparation"].push({
            timeInSeconds: processingTimeInSeconds
        });

        // Keep only the last 5 entries
        if (processingTimeHistory["teethseparation"].length > 5) {
            processingTimeHistory["teethseparation"].shift();
        }

        // Short delay to show 100% completion before hiding
        await new Promise((resolve) => setTimeout(resolve, 750));

        // Hide processing indicator to show results
        processingIndicator.style.display = "none";

        // Process the results and set up the multi-view display
        initMultiViewRendering();
        setupMultiViewContent(processingResult);

        showToast(
            "Teeth reconstruction completed successfully",
            "check-circle"
        );
    } catch (error) {
        console.error("Error during teeth reconstruction:", error);
        showToast(
            "Error during teeth reconstruction: " + error.message,
            "exclamation-triangle"
        );

        // Make sure to hide the indicator on error
        processingIndicator.style.display = "none";
        document.getElementById("next-phase-button").style.display = "block";
        document.getElementById("next-phase-button").textContent = "Try Again";
    }
}

// Setup content for multi-view panels
function setupMultiViewContent(processingResult) {
    // Store reconstruction result
    globalState.separationData.reconstructionResult =
        processingResult.reconstruction_result;

    // Add detailed logging
    console.log("Setting up multi-view content");
    console.log(
        "Reconstruction result available:",
        !!processingResult.reconstruction_result
    );
    console.log(
        "Reconstruction result:",
        processingResult.reconstruction_result
    );

    // Find the teeth data in the reconstruction result
    let teethData = null;

    if (processingResult.reconstruction_result) {
        console.log(
            "Reconstruction result keys:",
            Object.keys(processingResult.reconstruction_result)
        );

        // Check various possible locations for teeth data
        if (processingResult.reconstruction_result.teeth) {
            // Check if teeth is an object with numeric keys instead of a standard array
            if (
                typeof processingResult.reconstruction_result.teeth ===
                    "object" &&
                !Array.isArray(processingResult.reconstruction_result.teeth)
            ) {
                // Convert object with numeric keys to array
                teethData = Object.keys(
                    processingResult.reconstruction_result.teeth
                )
                    .filter((key) => !isNaN(parseInt(key)))
                    .sort((a, b) => parseInt(a) - parseInt(b))
                    .map(
                        (key) =>
                            processingResult.reconstruction_result.teeth[key]
                    );
                console.log(
                    "Converted teeth object to array, length:",
                    teethData.length
                );
            } else {
                teethData = processingResult.reconstruction_result.teeth;
            }
            console.log("Found teeth data in reconstructionResult.teeth");
        } else if (
            processingResult.reconstruction_result.reconstruction_result &&
            processingResult.reconstruction_result.reconstruction_result.teeth
        ) {
            // Similar check for nested teeth data
            if (
                typeof processingResult.reconstruction_result
                    .reconstruction_result.teeth === "object" &&
                !Array.isArray(
                    processingResult.reconstruction_result.reconstruction_result
                        .teeth
                )
            ) {
                teethData = Object.keys(
                    processingResult.reconstruction_result.reconstruction_result
                        .teeth
                )
                    .filter((key) => !isNaN(parseInt(key)))
                    .sort((a, b) => parseInt(a) - parseInt(b))
                    .map(
                        (key) =>
                            processingResult.reconstruction_result
                                .reconstruction_result.teeth[key]
                    );
                console.log(
                    "Converted nested teeth object to array, length:",
                    teethData.length
                );
            } else {
                teethData =
                    processingResult.reconstruction_result.reconstruction_result
                        .teeth;
            }
            console.log(
                "Found teeth data in reconstructionResult.reconstruction_result.teeth"
            );
        } else if (
            processingResult.reconstruction_result.data &&
            processingResult.reconstruction_result.data.teeth
        ) {
            // Similar check for data.teeth
            if (
                typeof processingResult.reconstruction_result.data.teeth ===
                    "object" &&
                !Array.isArray(
                    processingResult.reconstruction_result.data.teeth
                )
            ) {
                teethData = Object.keys(
                    processingResult.reconstruction_result.data.teeth
                )
                    .filter((key) => !isNaN(parseInt(key)))
                    .sort((a, b) => parseInt(a) - parseInt(b))
                    .map(
                        (key) =>
                            processingResult.reconstruction_result.data.teeth[
                                key
                            ]
                    );
                console.log(
                    "Converted data.teeth object to array, length:",
                    teethData.length
                );
            } else {
                teethData = processingResult.reconstruction_result.data.teeth;
            }
            console.log("Found teeth data in reconstructionResult.data.teeth");
        } else {
            // Look for any property that might contain teeth data
            for (const key of Object.keys(
                processingResult.reconstruction_result
            )) {
                const value = processingResult.reconstruction_result[key];
                if (value && typeof value === "object") {
                    console.log(
                        `Checking property '${key}' for teeth data:`,
                        value
                    );

                    // If this property is an array, it might be the teeth data
                    if (
                        Array.isArray(value) &&
                        value.length > 0 &&
                        value[0].vertices
                    ) {
                        teethData = value;
                        console.log(
                            `Found possible teeth data in reconstructionResult.${key}`
                        );
                        break;
                    }

                    // Or it might be nested one level deeper
                    if (value.teeth && Array.isArray(value.teeth)) {
                        teethData = value.teeth;
                        console.log(
                            `Found teeth data in reconstructionResult.${key}.teeth`
                        );
                        break;
                    }

                    // Or it might be an object with numeric keys
                    if (key === "teeth" && typeof value === "object") {
                        const numericKeys = Object.keys(value).filter(
                            (k) => !isNaN(parseInt(k))
                        );
                        if (numericKeys.length > 0) {
                            teethData = numericKeys
                                .sort((a, b) => parseInt(a) - parseInt(b))
                                .map((k) => value[k]);
                            console.log(
                                `Converted object to array from reconstructionResult.${key}, length:`,
                                teethData.length
                            );
                            break;
                        }
                    }
                }
            }
        }

        console.log("Teeth data found:", !!teethData);
        if (teethData) {
            console.log("Teeth data length:", teethData.length);
            if (teethData.length > 0) {
                console.log(
                    "First tooth data keys:",
                    Object.keys(teethData[0])
                );
            }
        }
    }

    // 1. Original view - display the original mesh without colors
    if (globalState.separationData.originalMesh) {
        const originalModel = globalState.separationData.originalMesh.clone();
        originalModel.material = new THREE.MeshStandardMaterial({
            color: 0xcccccc,
            metalness: 0.2,
            roughness: 0.7
        });
        globalState.multiViewScenes.original.add(originalModel);
    } else {
        // If no original mesh saved (should not happen), display the current mesh
        const originalModel = globalState.mesh.clone();
        originalModel.material = new THREE.MeshStandardMaterial({
            color: 0xcccccc,
            metalness: 0.2,
            roughness: 0.7
        });
        globalState.multiViewScenes.original.add(originalModel);
    }

    // 2. Segmented view - display segmented mesh with colors
    const segmentedModel = globalState.mesh.clone();
    globalState.multiViewScenes.segmented.add(segmentedModel);

    // Add labels to segmented view
    if (
        processingResult.reconstruction_result &&
        processingResult.reconstruction_result.labels
    ) {
        addLabelsToSegmentedView(
            segmentedModel,
            processingResult.reconstruction_result
        );
    }

    // 3 & 4. Analysis and Final views - display the reconstructed result
    if (teethData && teethData.length > 0) {
        console.log("Setting up teeth reconstruction with data:", {
            teethCount: teethData.length,
            firstToothSample: teethData[0],
            hasGingiva: !!(
                processingResult.reconstruction_result.gingiva ||
                (processingResult.reconstruction_result &&
                    processingResult.reconstruction_result.gingiva)
            ),
            hasObjFile: !!(
                processingResult.final_obj ||
                (processingResult.reconstruction_result &&
                    processingResult.reconstruction_result.final_obj)
            )
        });

        // Debug: check if the teeth data contains properly formatted vertices/faces
        const firstTooth = teethData[0];
        console.log("First tooth vertex format check:", {
            hasVertices: !!firstTooth.vertices,
            verticesType: typeof firstTooth.vertices,
            isVerticesArray: Array.isArray(firstTooth.vertices),
            verticesLength: firstTooth.vertices
                ? firstTooth.vertices.length
                : 0,
            isVerticesEncoded:
                firstTooth.vertices &&
                firstTooth.vertices.encoding &&
                firstTooth.vertices.data,
            hasFaces: !!firstTooth.faces,
            facesType: typeof firstTooth.faces,
            isFacesArray: Array.isArray(firstTooth.faces),
            facesLength: firstTooth.faces ? firstTooth.faces.length : 0,
            isFacesEncoded:
                firstTooth.faces &&
                firstTooth.faces.encoding &&
                firstTooth.faces.data
        });

        try {
            // Find gingiva data
            let gingivaData = null;
            if (processingResult.reconstruction_result.gingiva) {
                gingivaData = processingResult.reconstruction_result.gingiva;
            } else if (
                processingResult.reconstruction_result.reconstruction_result &&
                processingResult.reconstruction_result.reconstruction_result
                    .gingiva
            ) {
                gingivaData =
                    processingResult.reconstruction_result.reconstruction_result
                        .gingiva;
            }

            // Add teeth with roots to analysis view (100% opaque)
            addReconstructedTeeth(
                globalState.multiViewScenes.analysis,
                teethData,
                false
            );

            // Add root direction arrows to analysis view
            addRootDirectionArrows(teethData);

            // Add teeth with roots to final view (solid)
            addReconstructedTeeth(
                globalState.multiViewScenes.final,
                teethData,
                false
            );

            // Add gingiva to final view if available (80% opacity)
            if (gingivaData) {
                addGingiva(globalState.multiViewScenes.final, gingivaData);
            }

            // Show reconstruction timings if available
            let timings = null;
            if (processingResult.reconstruction_result.timings) {
                timings = processingResult.reconstruction_result.timings;
            } else if (
                processingResult.reconstruction_result.reconstruction_result &&
                processingResult.reconstruction_result.reconstruction_result
                    .timings
            ) {
                timings =
                    processingResult.reconstruction_result.reconstruction_result
                        .timings;
            }

            if (timings) {
                const timingInfo = document.createElement("div");
                timingInfo.classList.add("timing-info");
                timingInfo.style.position = "absolute";
                timingInfo.style.bottom = "10px";
                timingInfo.style.right = "10px";
                timingInfo.style.background = "rgba(0,0,0,0.7)";
                timingInfo.style.color = "white";
                timingInfo.style.padding = "10px";
                timingInfo.style.borderRadius = "5px";
                timingInfo.style.fontSize = "12px";

                let timingText = "<strong>Reconstruction Timings:</strong><br>";
                for (const [key, value] of Object.entries(timings)) {
                    timingText += `${key}: ${value.toFixed(2)}s<br>`;
                }
                timingInfo.innerHTML = timingText;
                document.getElementById("final-view").appendChild(timingInfo);
            }
        } catch (error) {
            console.error("Error setting up reconstructed teeth:", error);
            showToast(
                "Error displaying reconstructed teeth: " + error.message,
                "exclamation-triangle"
            );

            // Fallback to segmented model if error occurs
            const analysisModel = globalState.mesh.clone();
            analysisModel.material = new THREE.MeshStandardMaterial({
                vertexColors: true,
                metalness: 0.2,
                roughness: 0.7
            });
            globalState.multiViewScenes.analysis.add(analysisModel);

            const finalModel = globalState.mesh.clone();
            finalModel.material = new THREE.MeshStandardMaterial({
                vertexColors: true,
                metalness: 0.3,
                roughness: 0.5
            });
            globalState.multiViewScenes.final.add(finalModel);
        }
    } else {
        console.warn(
            "No valid teeth reconstruction data. Using fallback segmented model."
        );
        // Fallback if no reconstruction result
        const analysisModel = globalState.mesh.clone();
        analysisModel.material = new THREE.MeshStandardMaterial({
            vertexColors: true,
            metalness: 0.2,
            roughness: 0.7
        });
        globalState.multiViewScenes.analysis.add(analysisModel);

        const finalModel = globalState.mesh.clone();
        finalModel.material = new THREE.MeshStandardMaterial({
            vertexColors: true,
            metalness: 0.3,
            roughness: 0.5
        });
        globalState.multiViewScenes.final.add(finalModel);
    }

    // Add call to show download buttons at the end of setup
    showDownloadButton();
}

// Add reconstructed teeth to scene
function addReconstructedTeeth(scene, teethData, isTransparent) {
    if (!teethData || !teethData.length) {
        console.error("No teeth data available or teeth data is empty");
        showToast(
            "Error: No valid teeth data received",
            "exclamation-triangle"
        );
        return;
    }

    console.log(`Adding ${teethData.length} teeth to scene`);
    let validTeethCount = 0;
    let skippedTeethCount = 0;

    teethData.forEach((tooth, index) => {
        console.log(
            `Processing tooth ${index} with instance ID ${
                tooth.instance_id || "unknown"
            }`
        );

        try {
            // Deep clone the tooth data to avoid modifying the original
            const toothCopy = JSON.parse(JSON.stringify(tooth));

            // Check if tooth vertices/faces need decoding (might be compressed in nested data)
            if (
                toothCopy.vertices &&
                toothCopy.vertices.encoding &&
                toothCopy.vertices.data
            ) {
                console.log(`Decoding compressed tooth ${index} vertices data`);
                try {
                    toothCopy.vertices = decodeNestedDictBase64(
                        toothCopy.vertices
                    );
                    console.log(
                        `Successfully decoded vertices for tooth ${index}, got ${toothCopy.vertices.length} vertices`
                    );
                } catch (error) {
                    console.error(
                        `Failed to decode vertices for tooth ${index}:`,
                        error
                    );
                    return; // Skip this tooth
                }
            }

            if (
                toothCopy.faces &&
                toothCopy.faces.encoding &&
                toothCopy.faces.data
            ) {
                console.log(`Decoding compressed tooth ${index} faces data`);
                try {
                    toothCopy.faces = decodeNestedDictBase64(toothCopy.faces);
                    console.log(
                        `Successfully decoded faces for tooth ${index}, got ${toothCopy.faces.length} faces`
                    );
                } catch (error) {
                    console.error(
                        `Failed to decode faces for tooth ${index}:`,
                        error
                    );
                    return; // Skip this tooth
                }
            }

            // Validate vertex and face data
            if (
                !toothCopy.vertices ||
                !Array.isArray(toothCopy.vertices) ||
                toothCopy.vertices.length === 0
            ) {
                console.warn(
                    `Skipping tooth ${index} (ID: ${toothCopy.instance_id}) - Missing or invalid vertices`
                );
                skippedTeethCount++;
                return;
            }

            if (
                !toothCopy.faces ||
                !Array.isArray(toothCopy.faces) ||
                toothCopy.faces.length === 0
            ) {
                console.warn(
                    `Skipping tooth ${index} (ID: ${toothCopy.instance_id}) - Missing or invalid faces`
                );
                skippedTeethCount++;
                return;
            }

            // Ensure vertices are in the correct format (array of [x,y,z] arrays)
            if (
                !Array.isArray(toothCopy.vertices[0]) ||
                toothCopy.vertices[0].length !== 3
            ) {
                console.warn(
                    `Tooth ${index} has invalid vertex format:`,
                    toothCopy.vertices[0]
                );
                skippedTeethCount++;
                return;
            }

            // Create geometry
            const geometry = new THREE.BufferGeometry();

            // Add vertices
            try {
                const vertices = new Float32Array(toothCopy.vertices.flat());
                geometry.setAttribute(
                    "position",
                    new THREE.BufferAttribute(vertices, 3)
                );
            } catch (error) {
                console.error(
                    `Error creating vertex buffer for tooth ${index}:`,
                    error
                );
                skippedTeethCount++;
                return;
            }

            // Add faces
            try {
                const indices = new Uint32Array(toothCopy.faces.flat());
                geometry.setIndex(new THREE.BufferAttribute(indices, 1));
            } catch (error) {
                console.error(
                    `Error creating face indices for tooth ${index}:`,
                    error
                );
                skippedTeethCount++;
                return;
            }

            // Compute normals for proper lighting
            try {
                geometry.computeVertexNormals();
            } catch (error) {
                console.error(
                    `Error computing normals for tooth ${index}:`,
                    error
                );
                // Continue anyway as this is not critical
            }

            // Determine tooth material based on type and transparency
            let color = getToothColor(toothCopy.class_id);
            let material;

            if (isTransparent) {
                material = new THREE.MeshPhongMaterial({
                    color: color,
                    transparent: true,
                    opacity: 0.7,
                    side: THREE.DoubleSide
                });
            } else {
                material = new THREE.MeshPhongMaterial({
                    color: color,
                    side: THREE.DoubleSide
                });
            }

            const mesh = new THREE.Mesh(geometry, material);
            mesh.userData = {
                instanceId: toothCopy.instance_id,
                classId: toothCopy.class_id,
                type: "tooth"
            };

            scene.add(mesh);
            validTeethCount++;
            console.log(
                `Successfully added tooth ${index} (ID: ${toothCopy.instance_id}) to scene`
            );
        } catch (error) {
            console.error(`Unexpected error processing tooth ${index}:`, error);
            skippedTeethCount++;
        }
    });

    console.log(
        `Added ${validTeethCount} teeth to scene, skipped ${skippedTeethCount} teeth with issues`
    );

    if (validTeethCount === 0) {
        console.error("No valid teeth with geometry were found");
        showToast(
            "Error: No valid teeth with geometry received",
            "exclamation-triangle"
        );
    } else {
        console.log(`Successfully added ${validTeethCount} teeth to the scene`);
    }
}

// Add gingiva to scene
function addGingiva(scene, gingivaData) {
    console.log("Adding gingiva to scene, data available:", !!gingivaData);

    try {
        // Deep clone the gingiva data to avoid modifying the original
        const gingivaCopy = JSON.parse(JSON.stringify(gingivaData));

        // Check if gingiva data needs decoding (might be compressed in nested data)
        if (
            gingivaCopy &&
            gingivaCopy.vertices &&
            gingivaCopy.vertices.encoding &&
            gingivaCopy.vertices.data
        ) {
            console.log("Decoding compressed gingiva vertices data");
            try {
                gingivaCopy.vertices = decodeNestedDictBase64(
                    gingivaCopy.vertices
                );
                console.log(
                    `Successfully decoded gingiva vertices, got ${gingivaCopy.vertices.length} vertices`
                );
            } catch (error) {
                console.error("Failed to decode gingiva vertices:", error);
                showToast(
                    "Error decoding gingiva data",
                    "exclamation-triangle"
                );
                return;
            }
        }

        if (
            gingivaCopy &&
            gingivaCopy.faces &&
            gingivaCopy.faces.encoding &&
            gingivaCopy.faces.data
        ) {
            console.log("Decoding compressed gingiva faces data");
            try {
                gingivaCopy.faces = decodeNestedDictBase64(gingivaCopy.faces);
                console.log(
                    `Successfully decoded gingiva faces, got ${gingivaCopy.faces.length} faces`
                );
            } catch (error) {
                console.error("Failed to decode gingiva faces:", error);
                showToast(
                    "Error decoding gingiva data",
                    "exclamation-triangle"
                );
                return;
            }
        }

        // Validate gingiva data
        if (
            !gingivaCopy ||
            !gingivaCopy.vertices ||
            !Array.isArray(gingivaCopy.vertices) ||
            gingivaCopy.vertices.length === 0
        ) {
            console.warn("No valid gingiva vertices available");
            return;
        }

        if (
            !gingivaCopy.faces ||
            !Array.isArray(gingivaCopy.faces) ||
            gingivaCopy.faces.length === 0
        ) {
            console.warn("No valid gingiva faces available");
            return;
        }

        // Ensure vertices are in the correct format (array of [x,y,z] arrays)
        if (
            !Array.isArray(gingivaCopy.vertices[0]) ||
            gingivaCopy.vertices[0].length !== 3
        ) {
            console.warn(
                "Gingiva has invalid vertex format:",
                gingivaCopy.vertices[0]
            );
            return;
        }

        // Create geometry
        const geometry = new THREE.BufferGeometry();

        // Add vertices
        try {
            const vertices = new Float32Array(gingivaCopy.vertices.flat());
            geometry.setAttribute(
                "position",
                new THREE.BufferAttribute(vertices, 3)
            );
            console.log("Successfully created gingiva vertices buffer");
        } catch (error) {
            console.error("Error creating gingiva vertex buffer:", error);
            return;
        }

        // Add faces
        try {
            const indices = new Uint32Array(gingivaCopy.faces.flat());
            geometry.setIndex(new THREE.BufferAttribute(indices, 1));
            console.log("Successfully created gingiva face indices buffer");
        } catch (error) {
            console.error("Error creating gingiva face indices:", error);
            return;
        }

        // Compute normals for proper lighting
        try {
            geometry.computeVertexNormals();
        } catch (error) {
            console.error("Error computing gingiva normals:", error);
            // Continue anyway as this is not critical
        }

        // Create a material for gingiva - pinkish color
        const material = new THREE.MeshPhongMaterial({
            color: 0xf7a8b8, // Light pink color for gingiva
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.8
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.userData = {
            type: "gingiva"
        };

        scene.add(mesh);
        console.log("Successfully added gingiva to the scene");
    } catch (error) {
        console.error("Unexpected error adding gingiva:", error);
        showToast("Error adding gingiva to scene", "exclamation-triangle");
    }
}

// Add root direction arrows to the analysis view
function addRootDirectionArrows(teethData) {
    if (!teethData || !teethData.length) {
        console.warn("No teeth data available for root direction arrows");
        return;
    }

    teethData.forEach((tooth) => {
        if (
            !tooth.root_direction ||
            !Array.isArray(tooth.root_direction) ||
            tooth.root_direction.length !== 3
        ) {
            return; // Skip if no valid root direction
        }

        // Get tooth position (center)
        let center = new THREE.Vector3(0, 0, 0);
        if (tooth.vertices) {
            // If vertices are available, compute center from vertices
            let vertices = tooth.vertices;
            // Check if vertices need decoding
            if (vertices.encoding && vertices.data) {
                vertices = decodeNestedDictBase64(vertices);
            }

            if (Array.isArray(vertices) && vertices.length > 0) {
                const vertexCount = vertices.length;
                for (let i = 0; i < vertexCount; i++) {
                    center.x += vertices[i][0];
                    center.y += vertices[i][1];
                    center.z += vertices[i][2];
                }
                center.divideScalar(vertexCount);
            }
        }

        // Create arrow from center in the direction of root_direction
        const rootDir = new THREE.Vector3(
            tooth.root_direction[0],
            tooth.root_direction[1],
            tooth.root_direction[2]
        ).normalize();

        // Arrow length should be proportional to tooth size
        const arrowLength = 5; // Default value
        const arrowHelper = new THREE.ArrowHelper(
            rootDir,
            center,
            arrowLength,
            0xff0000, // Red color for root direction
            arrowLength * 0.2, // Head length
            arrowLength * 0.1 // Head width
        );

        // Add arrow to analysis view
        globalState.multiViewScenes.analysis.add(arrowHelper);
    });
}

// Function to show the download button
function showDownloadButton() {
    // Remove existing container if present
    const existingContainer = document.getElementById(
        "download-buttons-container"
    );
    if (existingContainer) {
        existingContainer.remove();
    }

    // Create a container for the buttons
    const buttonContainer = document.createElement("div");
    buttonContainer.id = "download-buttons-container";

    // Create JSON download button
    const downloadLabelsButton = document.createElement("button");
    downloadLabelsButton.id = "download-results-button";
    downloadLabelsButton.textContent = "Download Labels JSON";
    downloadLabelsButton.addEventListener("click", downloadLabelsJSON);
    buttonContainer.appendChild(downloadLabelsButton);

    // Find the final_obj data in potentially nested structures
    let finalObjData = null;
    if (
        globalState.separationData &&
        globalState.separationData.reconstructionResult
    ) {
        const reconstructionResult =
            globalState.separationData.reconstructionResult;

        if (reconstructionResult.final_obj) {
            finalObjData = reconstructionResult.final_obj;
            console.log("Found final_obj in reconstructionResult.final_obj");
        } else if (
            reconstructionResult.reconstruction_result &&
            reconstructionResult.reconstruction_result.final_obj
        ) {
            finalObjData = reconstructionResult.reconstruction_result.final_obj;
            console.log(
                "Found final_obj in reconstructionResult.reconstruction_result.final_obj"
            );
        } else {
            // Look for final_obj in any property
            for (const key of Object.keys(reconstructionResult)) {
                const value = reconstructionResult[key];
                if (value && typeof value === "object" && value.final_obj) {
                    finalObjData = value.final_obj;
                    console.log(
                        `Found final_obj in reconstructionResult.${key}.final_obj`
                    );
                    break;
                }
            }
        }
    }

    // Create OBJ download button if final_obj data is available
    if (finalObjData) {
        const downloadObjButton = document.createElement("button");
        downloadObjButton.id = "download-obj-button";
        downloadObjButton.textContent = "Download Reconstructed OBJ";
        downloadObjButton.addEventListener("click", downloadReconstructedObj);
        buttonContainer.appendChild(downloadObjButton);
        console.log("Added Download OBJ button, final_obj is available");
    } else {
        console.log(
            "No final_obj available in reconstruction result:",
            globalState.separationData?.reconstructionResult
        );
    }

    // Add the container to the body
    document.body.appendChild(buttonContainer);
}

// Function to download the segmentation results as JSON
function downloadLabelsJSON() {
    if (
        !globalState.lastInferenceResult ||
        !globalState.lastInferenceResult.labels
    ) {
        showToast(
            "No segmentation results to download",
            "exclamation-triangle"
        );
        return;
    }

    // Include all fields for user download
    const jsonContent = createLabelsJSON([
        "is_compressed",
        "labels",
        "instance_ids",
        "class_ids",
        "class_names",
        "class_colors",
        "boundary_points",
        "simplified_boundary_points"
    ]);

    // Create and download file
    const fileName =
        document
            .getElementById("file-select")
            .files[0].name.replace(/\.[^/.]+$/, "") + "_labels.json";
    const blob = new Blob([JSON.stringify(jsonContent, null, 2)], {
        type: "application/json"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// Function to download the reconstructed 3D model as OBJ file
function downloadReconstructedObj() {
    let finalObjData = null;

    // Find the final_obj data in potentially nested structures
    if (
        globalState.separationData &&
        globalState.separationData.reconstructionResult
    ) {
        const reconstructionResult =
            globalState.separationData.reconstructionResult;

        if (reconstructionResult.final_obj) {
            finalObjData = reconstructionResult.final_obj;
        } else if (
            reconstructionResult.reconstruction_result &&
            reconstructionResult.reconstruction_result.final_obj
        ) {
            finalObjData = reconstructionResult.reconstruction_result.final_obj;
        } else {
            // Look for final_obj in any property
            for (const key of Object.keys(reconstructionResult)) {
                const value = reconstructionResult[key];
                if (value && typeof value === "object" && value.final_obj) {
                    finalObjData = value.final_obj;
                    break;
                }
            }
        }
    }

    if (!finalObjData) {
        showToast(
            "No reconstruction data available to download",
            "exclamation-triangle"
        );
        return;
    }

    try {
        // Decode base64 to binary string
        const binaryString = atob(finalObjData);

        // Convert binary string to array buffer
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        // Create blob and download
        const blob = new Blob([bytes], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);

        // Create filename based on original file
        const fileName =
            document
                .getElementById("file-select")
                .files[0].name.replace(/\.[^/.]+$/, "") + "_reconstructed.obj";

        // Create download link
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        showToast("Reconstructed OBJ downloaded successfully", "check-circle");
    } catch (error) {
        console.error("Error downloading OBJ file:", error);
        showToast(
            "Error downloading OBJ file: " + error.message,
            "exclamation-triangle"
        );
    }
}

// Get color for tooth based on class ID
function getToothColor(classId) {
    // Map from FDI tooth numbering to consistent colors
    const colors = {
        // Upper right quadrant (1)
        11: 0x3498db, // central incisor - soft blue
        12: 0x2ecc71, // lateral incisor - emerald green
        13: 0xf1c40f, // canine - sunshine yellow
        14: 0xe74c3c, // first premolar - soft red
        15: 0x9b59b6, // second premolar - amethyst purple
        16: 0x1abc9c, // first molar - turquoise
        17: 0xd35400, // second molar - pumpkin orange
        18: 0x34495e, // third molar - wet asphalt blue

        // Upper left quadrant (2)
        21: 0x3498db, // central incisor - soft blue
        22: 0x2ecc71, // lateral incisor - emerald green
        23: 0xf1c40f, // canine - sunshine yellow
        24: 0xe74c3c, // first premolar - soft red
        25: 0x9b59b6, // second premolar - amethyst purple
        26: 0x1abc9c, // first molar - turquoise
        27: 0xd35400, // second molar - pumpkin orange
        28: 0x34495e, // third molar - wet asphalt blue

        // Lower left quadrant (3)
        31: 0x3498db, // central incisor - soft blue
        32: 0x2ecc71, // lateral incisor - emerald green
        33: 0xf1c40f, // canine - sunshine yellow
        34: 0xe74c3c, // first premolar - soft red
        35: 0x9b59b6, // second premolar - amethyst purple
        36: 0x1abc9c, // first molar - turquoise
        37: 0xd35400, // second molar - pumpkin orange
        38: 0x34495e, // third molar - wet asphalt blue

        // Lower right quadrant (4)
        41: 0x3498db, // central incisor - soft blue
        42: 0x2ecc71, // lateral incisor - emerald green
        43: 0xf1c40f, // canine - sunshine yellow
        44: 0xe74c3c, // first premolar - soft red
        45: 0x9b59b6, // second premolar - amethyst purple
        46: 0x1abc9c, // first molar - turquoise
        47: 0xd35400, // second molar - pumpkin orange
        48: 0x34495e // third molar - wet asphalt blue
    };

    // Return specific color if defined, or generate one based on class ID
    if (colors[classId]) {
        return colors[classId];
    } else {
        // Generate a more vibrant color based on the classId
        const hue = (classId % 10) / 10;
        return new THREE.Color().setHSL(hue, 0.9, 0.6).getHex();
    }
}

// Function to add labels to the segmented view
function addLabelsToSegmentedView(mesh, inferenceResult) {
    if (!mesh || !inferenceResult || !inferenceResult.labels) return;

    const geometry = mesh.geometry;
    const labelPositions = {};

    // Calculate average position for each class
    for (let i = 0; i < inferenceResult.labels.length; i++) {
        const label = inferenceResult.labels[i];
        if (!labelPositions[label]) {
            labelPositions[label] = { sum: new THREE.Vector3(), count: 0 };
        }
        const vertex = new THREE.Vector3().fromBufferAttribute(
            geometry.attributes.position,
            i
        );
        labelPositions[label].sum.add(vertex);
        labelPositions[label].count++;
    }

    // Get appropriate class data
    const processingMode = "teethml"; // Always teeth for separation mode
    const classesData = toothClasses;

    // Create and position class labels
    Object.entries(labelPositions).forEach(([label, data]) => {
        const avgPosition = data.sum.divideScalar(data.count);
        const classData = classesData.find((c) => c.id === parseInt(label));

        if (classData) {
            const labelDiv = document.createElement("div");
            labelDiv.className = "tooth-label";
            labelDiv.textContent = classData.name;
            labelDiv.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
            labelDiv.style.color = "white";
            labelDiv.style.padding = "2px 5px";
            labelDiv.style.borderRadius = "3px";
            labelDiv.style.fontSize = "12px";
            labelDiv.style.fontWeight = "bold";

            const labelObject = new CSS2DObject(labelDiv);
            labelObject.position.copy(avgPosition);

            globalState.multiViewScenes.segmented.add(labelObject);
        }
    });
}

export {
    showDownloadButton,
    downloadLabelsJSON,
    downloadReconstructedObj,
    processSeparation
};
